import { cacheQuestions, enforcePackLimit, pruneCachedSubjectsForMode, recomputePackMeta } from './offlineDb'
import { requestOfflineReadinessFill } from './offlinePack'
import { track } from './analytics'

export type OfflineMode = 'admission' | 'national_12'
export type OfflineReadinessPriority = 'urgent' | 'background'

export const OFFLINE_PACK_REFRESH_STARTED_EVENT = 'tutor:offline-pack-refresh-started'
export const OFFLINE_PACK_REFRESH_FINISHED_EVENT = 'tutor:offline-pack-refresh-finished'

interface EnsureOfflineReadinessArgs {
  priority: OfflineReadinessPriority
  mode: OfflineMode
  university?: string | null
  activeSubjectCode?: string | null
  subjectCodes?: string[]
  /**
   * Whose UEM borrow opt-ins this fill may act on. Omitted means none: a
   * pack must not inherit the consent of whoever used the device last.
   */
  studentId?: string | null
  /**
   * The admin cap as a TOTAL across every mode, not this fill's slice.
   * Passing a per-mode share here would make each fill trim the whole store
   * down to that share, so the last mode to finish would evict the others.
   */
  maxQuestions?: number
  /**
   * Every mode the student can reach, so enforcePackLimit can water-fill the
   * cap between them. Without it the trim is mode-blind and oldest-first,
   * which deletes the other mode's questions rather than this fill's excess.
   */
  packModes?: OfflineMode[]
  force?: boolean
}

const SUBJECTS_PREFETCH_KEY = 'subjects.prefetch.v1'
const PREFETCH_TTL_MS = 30 * 60_000
const BACKGROUND_COOLDOWN_MS = 60_000
const URGENT_PREFETCH_CAP = 3
const URGENT_PREFETCH_COUNT = 20

// Mode-scoped guards prevent different readiness callers from trampling each
// other if they request separate caches.
const backgroundPromises = new Map<OfflineMode, Promise<void>>()
const urgentPromises = new Map<OfflineMode, Promise<void>>()
const lastBackgroundStartedAt = new Map<OfflineMode, number>()

// Refresh events are per-pack, but the UI's "downloading" flag is one boolean
// for the whole app. With two fills in flight, dispatching FINISHED when the
// first completes would clear the spinner while the second is still running.
// Count instead: STARTED on 0 -> 1, FINISHED on 1 -> 0.
let inFlightBackgroundFills = 0

// Circuit breaker: once the backend tells us this student is genuinely
// ineligible for offline packs we stop firing /offline-readiness/fill for
// the rest of the session. Narrow to specific codes on purpose: an earlier
// version tripped on any 403, which killed free-tier auto-downloads for the
// whole session the moment an unrelated auth blip returned 403.
//
//   subscription_required   tier truly has offline_packs = false
//   pack_limit_zero         misconfigured tier
//   not_available_anonymous the route is enforceNonAnonymous
//
// The last one is defence in depth. Callers now check is_anonymous before
// firing, so it should be unreachable; it is listed because when it did
// happen the request retried instead of stopping, and a guaranteed 403 in a
// retry loop is the worst shape a background job can take.
let ineligibleForOfflinePacks = false

function isBrowserReady(): boolean {
  return typeof window !== 'undefined' && typeof navigator !== 'undefined'
}

function dispatchOfflinePackRefresh(eventName: string) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(eventName))
  }
}

function canUseConnectionForBackground(): boolean {
  const conn = (navigator as Navigator & {
    connection?: { type?: string; saveData?: boolean }
  }).connection
  if (conn?.saveData) return false
  return true
}

function normalizeSubjectCodes(args: EnsureOfflineReadinessArgs, limit: number): string[] {
  const seen = new Set<string>()
  const ordered = [args.activeSubjectCode ?? null, ...(args.subjectCodes ?? [])]
  const next: string[] = []
  for (const code of ordered) {
    if (!code || seen.has(code)) continue
    seen.add(code)
    next.push(code)
    if (next.length >= limit) break
  }
  return next
}

function readPrefetchCache(): Record<string, number> {
  try {
    const raw = sessionStorage.getItem(SUBJECTS_PREFETCH_KEY)
    return raw ? JSON.parse(raw) as Record<string, number> : {}
  } catch {
    return {}
  }
}

function prefetchCacheKey(mode: OfflineMode, university: string | null | undefined, subjectCode: string): string {
  return `${mode}:${university ?? 'all'}:${subjectCode}`
}

function shouldPrefetchSubject(subjectCode: string, mode: OfflineMode, university: string | null | undefined, force = false): boolean {
  if (force) return true
  const cache = readPrefetchCache()
  const last = cache[prefetchCacheKey(mode, university, subjectCode)] ?? 0
  return !last || (Date.now() - last) > PREFETCH_TTL_MS
}

function markPrefetchSubject(subjectCode: string, mode: OfflineMode, university: string | null | undefined) {
  try {
    const cache = readPrefetchCache()
    cache[prefetchCacheKey(mode, university, subjectCode)] = Date.now()
    sessionStorage.setItem(SUBJECTS_PREFETCH_KEY, JSON.stringify(cache))
  } catch {
    /* best-effort cache */
  }
}

async function urgentFill(args: EnsureOfflineReadinessArgs) {
  // Urgent fill only warms the active subject.
  const subjectCodes = normalizeSubjectCodes(args, URGENT_PREFETCH_CAP)
  if (subjectCodes.length === 0) return

  const university = args.university ?? null
  const prefetched = subjectCodes.filter((subjectCode) =>
    shouldPrefetchSubject(subjectCode, args.mode, university, args.force),
  )
  if (prefetched.length === 0) return

  try {
    const response = await requestOfflineReadinessFill({
      priority: 'urgent',
      mode: args.mode,
      university,
      activeSubjectCode: args.activeSubjectCode ?? null,
      studentId: args.studentId ?? null,
      subjectCodes: prefetched,
      countPerSubject: URGENT_PREFETCH_COUNT,
    })
    for (const bucket of response.buckets) {
      await cacheQuestions(bucket.subject_code, bucket.questions, {
        mode: args.mode,
        origin: 'readiness_fill',
        cachedAtBand: bucket.cached_at_band,
        replaceExisting: false,
      })
      markPrefetchSubject(bucket.subject_code, args.mode, university)
    }
    if ((args.maxQuestions ?? 0) > 0) {
      await enforcePackLimit(args.maxQuestions!, args.packModes)
    }
  } catch (err: any) {
    // Only trip on the two error codes we know are stable per-session:
    // subscription_required (tier truly has offline_packs=false) and
    // pack_limit_zero (misconfigured tier).
    const code = typeof err?.data?.error === 'string' ? err.data.error : null
    if (err?.status === 403 && (code === 'subscription_required' || code === 'pack_limit_zero' || code === 'not_available_anonymous')) {
      ineligibleForOfflinePacks = true
    }
    /* silent warmup */
  }
}

async function backgroundFill(args: EnsureOfflineReadinessArgs) {
  // Background fill refreshes the broader programme inventory.
  try {
    const response = await requestOfflineReadinessFill({
      priority: 'background',
      mode: args.mode,
      university: args.university ?? null,
      activeSubjectCode: args.activeSubjectCode ?? null,
      studentId: args.studentId ?? null,
    })
    // Only the programme names are taken from the response now. Counts and
    // subjects are derived from the cache by recomputePackMeta below, because
    // this fill only knows its own mode's slice.
    const programmeNames = response.fill?.programme_names ?? []

    // `cacheQuestions(replaceExisting: true)` refreshes touched buckets.
    // This extra prune removes dropped subjects for the current mode only.
    const freshSubjects = response.buckets.map(b => b.subject_code)
    await pruneCachedSubjectsForMode(freshSubjects, args.mode)

    for (const bucket of response.buckets) {
      await cacheQuestions(bucket.subject_code, bucket.questions, {
        mode: args.mode,
        origin: 'readiness_fill',
        cachedAtBand: bucket.cached_at_band,
        replaceExisting: true,
      })
    }
    if ((args.maxQuestions ?? 0) > 0) {
      await enforcePackLimit(args.maxQuestions!, args.packModes)
    }

    // Derived from the store rather than written from this fill's slice.
    // PackMeta is a singleton, so with two modes in flight each writer would
    // otherwise claim the whole pack and the last one would win with a
    // partial count. Recomputing reads the union, so whichever fill lands
    // last still produces correct totals. Runs after the cache writes so it
    // never advertises questions that are not yet stored.
    await recomputePackMeta(programmeNames)
  } catch (err: any) {
    console.error('[offlineReadiness] backgroundFill failed', err)
    // Only trip on the two error codes we know are stable per-session:
    // subscription_required (tier truly has offline_packs=false) and
    // pack_limit_zero (misconfigured tier).
    const code = typeof err?.data?.error === 'string' ? err.data.error : null
    if (err?.status === 403 && (code === 'subscription_required' || code === 'pack_limit_zero' || code === 'not_available_anonymous')) {
      ineligibleForOfflinePacks = true
    }
    /* silent background refresh */
  }
}

// Used for telemetry only.
function readConnectionType(): string {
  const conn = (navigator as Navigator & { connection?: { effectiveType?: string; type?: string } }).connection
  return conn?.effectiveType ?? conn?.type ?? 'unknown'
}

export async function ensureOfflineReadiness(args: EnsureOfflineReadinessArgs): Promise<void> {
  if (!isBrowserReady() || !navigator.onLine) return
  if (ineligibleForOfflinePacks) return

  if (args.priority === 'urgent') {
    const inFlight = urgentPromises.get(args.mode)
    if (inFlight) return inFlight
    const startedAt = Date.now()
    const connectionType = readConnectionType()
    const run = urgentFill(args).finally(() => {
      urgentPromises.delete(args.mode)
      try {
        track('offline_topup_completed', {
          priority:        'urgent',
          mode:            args.mode,
          duration_ms:     Date.now() - startedAt,
          connection_type: connectionType,
          subject_count:   args.subjectCodes?.length ?? (args.activeSubjectCode ? 1 : 0),
        })
      } catch { /* analytics best-effort */ }
    })
    urgentPromises.set(args.mode, run)
    return run
  }

  const now = Date.now()
  const inFlight = backgroundPromises.get(args.mode)
  if (!args.force && inFlight) return inFlight
  const lastStart = lastBackgroundStartedAt.get(args.mode) ?? 0
  if (!args.force && now - lastStart < BACKGROUND_COOLDOWN_MS) return
  if (!canUseConnectionForBackground()) return

  lastBackgroundStartedAt.set(args.mode, now)
  const startedAt = Date.now()
  const connectionType = readConnectionType()
  inFlightBackgroundFills++
  if (inFlightBackgroundFills === 1) {
    dispatchOfflinePackRefresh(OFFLINE_PACK_REFRESH_STARTED_EVENT)
  }
  const run = backgroundFill(args).finally(() => {
    backgroundPromises.delete(args.mode)
    inFlightBackgroundFills = Math.max(0, inFlightBackgroundFills - 1)
    if (inFlightBackgroundFills === 0) {
      dispatchOfflinePackRefresh(OFFLINE_PACK_REFRESH_FINISHED_EVENT)
    }
    try {
      track('offline_topup_completed', {
        priority:        'background',
        mode:            args.mode,
        duration_ms:     Date.now() - startedAt,
        connection_type: connectionType,
      })
    } catch { /* analytics best-effort */ }
  })
  backgroundPromises.set(args.mode, run)
  return run
}
